import classNames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import { LayerManagerPortal, Layers } from 'components/layer-manager';
// eslint-disable-next-line no-restricted-imports
import { PopoverOwnerContext } from 'components/popover/context';
// eslint-disable-next-line no-restricted-imports
import Keybingings, { Key } from 'components/shared/keybindings';
// eslint-disable-next-line no-restricted-imports
import { closePopover, PopoverName, State } from 'modules';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { debounce } from 'underscore';
// eslint-disable-next-line no-restricted-imports
import preventDefault from 'util/prevent-default';

import { TestId } from '@trello/webdriverio-tests';
// eslint-disable-next-line no-restricted-imports
import { forNamespace } from 'common/lib/util/i18n';
// eslint-disable-next-line no-restricted-imports
import { Rect } from 'components/rect';
import PopoverConfirm, { PopoverConfirmProps } from './popover-confirm';
import PopoverHeader from './popover-header';
import styles from './popover.less';

// This allows the fancy scrollbar to show in its entirety
const POPOVER_PADDING = 17;

// For popovers with titles, we need to add more padding to make sure all contents
// when there is an overflow
const POPOVER_WITH_TITLE_PADDING = 33;

const DEFAULT_POPOVER_WIDTH = 304;

// Magic number alert! 47 is the height of the global header and a little
// extra padding for the bottom. we don't want the popover to overflow past
// the bottom of the screen.
const HEADER_HEIGHT_OFFSET = 42;

interface PopFn {
  (depth?: number): void;
}

export interface PushFn {
  (node: React.ReactNode, options?: PopoverLayerOptions): void;
}

export interface PushConfirmFn {
  (confirmProps: PopoverConfirmProps & { width?: number }): void;
}

interface PopoverLayerOptions {
  title?: React.ReactNode;
  width?: number;
}

interface PopoverLayer {
  node: React.ReactNode;
  options: PopoverLayerOptions;
}

interface OwnProps {
  alignRight?: boolean;

  // Normal behavior for popover is to receive focus.
  // Components like the Search bar do not behave like that.
  disableAutoFocus?: boolean;

  contentClassName?: string;
  children: (
    options: {
      maxHeight: number;
      pop: PopFn;
      push: PushFn;
      closePopover: () => void;
      pushConfirm: PushConfirmFn;
    },
  ) => React.ReactNode;
  className?: string;
  fixedContent?: React.ReactNode;
  left?: number;
  name: PopoverName;
  offsetLeft?: number;
  offsetTop?: number;
  onClose?: (trackingMethod: string) => void;
  showCloseButton?: boolean;
  testId?: TestId;
  title?: string;

  // `target` is a getter rather than a direct reference to the target
  // element since, often times, targets are defined in the same render
  // method as the popover itself. That means that you end up with a null
  // target, since refs are assigned during mount, not at render time.
  //
  // Example:
  //
  // targetRef = React.createRef();
  //
  // render() {
  //   return (
  //     <button ref={this.targetRef}>Open popover</button>
  //     <Popover target={this.targetRef.current}>Foo</Popover>
  //   )
  // }
  //
  // `this.targetRef.current` is null at render time, and the component does
  // not get re-rendered unless its props or state changes.
  target?: () => HTMLElement | null;

  // `relativeElement` is used when we want to use another element
  // as reference to positioning instead of `target`
  relativeElement?: () => HTMLElement | null;

  top?: number;
  width?: number;
}

interface ContextProps {
  addDescendant: (descendant: HTMLElement) => void;
  descendants: HTMLElement[];
  removeDescendant: (popoverElem: HTMLElement) => void;
}

interface DispatchProps {
  defaultOnClose: () => void;
}

export interface PopoverProps extends OwnProps, ContextProps, DispatchProps {}

interface PopoverState {
  left: number;
  maxHeight: number;
  stack: PopoverLayer[];
  top: number;
  width: number;
}

export class PopoverWithoutContext extends React.Component<
  PopoverProps,
  PopoverState
> {
  popoverElem: HTMLElement | null;
  contentRef = React.createRef<HTMLDivElement>();

  hasClosed = false;
  updateAfterNextRender = false;
  isComponentMounted = false;

  state: PopoverState = {
    left: 0,
    maxHeight: 0,
    stack: [],
    top: 0,
    width: 0,
  };

  componentDidMount() {
    this.isComponentMounted = true;
    // Event bubbling plays a key role in allowing us to support toggling
    // the display of a popover when clicking the popover's trigger element.

    // Because this listener is attached to the document, that means that
    // clicking the trigger for a popover while the popover is visible
    // causes the trigger's click handler to fire (showing the popover),
    // and then the document click handler fires, hiding the popover.

    // IMPORTANT: Using the capture phase here! This is because there are MANY
    // places in classic that are calling stopPropagation on clicks and preventing
    // popovers in Gamma from invoking handleOutsideClick and closing themselves
    document.addEventListener('click', this.handleOutsideClick, true);

    window.addEventListener('resize', this.debounceUpdateSizeAndPosition);

    this.updateSizeAndPosition();

    const popoverElem = this.popoverElem;

    if (popoverElem && !this.props.disableAutoFocus) {
      // move keyboard focus to the newly rendered popover
      popoverElem.focus();
    }
  }

  componentDidUpdate() {
    this.debounceUpdateSizeAndPosition();

    if (this.updateAfterNextRender) {
      // The popover needs to be rendered once to correctly calculate the
      // popover's height.
      this.updateAfterNextRender = false;
      this.updateSizeAndPositionOnce();
    }
  }

  componentWillUnmount() {
    this.isComponentMounted = false;

    // We do this to make sure that if a nested popover gets unmounted, we also
    // call `onClose` so that it doesn't automatically open if the parent popover
    // is opened later (since it will still be set as open in Redux)
    if (!this.hasClosed) {
      this.onClose();
    }

    if (this.popoverElem) {
      this.props.removeDescendant(this.popoverElem);
    }

    const target = this.props.target && this.props.target();

    if (!this.props.disableAutoFocus && target) {
      setImmediate(() => {
        if (target) {
          target.focus();
        }
      });
    }

    document.removeEventListener('click', this.handleOutsideClick, true);
    window.removeEventListener('resize', this.debounceUpdateSizeAndPosition);
  }

  push: PushFn = (node, options = {}) => {
    this.scrollContentToTop();

    this.setState(
      (state) => ({
        stack: [...state.stack, { node, options }],
      }),
      this.updateSizeAndPosition,
    );
  };

  pop: PopFn = (depth = 1) => {
    this.scrollContentToTop();

    /**
     * If there is a layer pushed onto the stack, pop it normally.
     * Otherwise, close the popover.
     */
    this.getCurrentLayer()
      ? this.setState(
          (state) => ({
            stack: state.stack.slice(0, state.stack.length - depth),
          }),
          this.updateSizeAndPosition,
        )
      : this.onClose();
  };

  pushConfirm: PushConfirmFn = ({ width, ...confirmProps }) => {
    this.push(<PopoverConfirm {...confirmProps} />, {
      title: forNamespace(['confirm', confirmProps.confirmKey])('title'),
      width,
    });
  };

  updateSizeAndPosition = () => {
    if (!this.isComponentMounted) {
      return false;
    }

    const changed = this.updateSizeAndPositionOnce();
    // The popover needs to be rendered once to correctly calculate the
    // popover's height
    if (changed) {
      this.updateAfterNextRender = true;
    }
  };

  // This sizing and positioning logic is lifted directly from the Classic
  // implementation of popovers. It has been adapted to use native DOM
  // methods and to only update dimensions after all measuring is complete.
  // Returns whether or not the size and position changed.
  updateSizeAndPositionOnce = (): boolean => {
    let width = DEFAULT_POPOVER_WIDTH;
    let top = this.props.top !== undefined ? this.props.top : -9999;
    let left = this.props.left !== undefined ? this.props.left : -9999;
    let offsetTop;
    const offsetLeft = this.props.offsetLeft || 0;

    const windowWidth = Math.round(window.innerWidth);
    const windowHeight = Math.round(window.innerHeight);

    const widthProp = this.getWidthProp();
    if (widthProp) {
      if (widthProp > windowWidth) {
        width = windowWidth - 16;
      } else {
        width = widthProp;
      }
    }

    const relativeElement =
      (this.props.relativeElement && this.props.relativeElement()) ||
      (this.props.target && this.props.target());
    if (relativeElement) {
      const relativeElementRect = relativeElement.getBoundingClientRect();
      if (typeof this.props.offsetTop === 'number') {
        offsetTop = this.props.offsetTop;
      } else {
        offsetTop = Math.round(relativeElementRect.height) + 6;
      }

      if (this.props.alignRight) {
        left =
          Math.round(relativeElementRect.left) +
          Math.round(relativeElementRect.width) -
          width;
      } else {
        left = Math.round(relativeElementRect.left);
      }

      left = left + offsetLeft;

      let popoverHeight = 0;
      if (this.popoverElem) {
        popoverHeight = Math.round(this.popoverElem.offsetHeight);
      }

      top = this.getIdealTopPosition(
        Math.round(relativeElementRect.top), // prevent partial pixel errors when not at 100% zoom
        offsetTop,
        windowHeight,
        popoverHeight,
      );
    }

    const maxHeight = windowHeight - (HEADER_HEIGHT_OFFSET + 5);

    // Ensure that the top of the element is visible
    top = top >= 0 ? top : 5;

    left = left >= 0 ? left : 5;
    const right = left + width + 16;

    // We want to ensure the right corner is on the screen
    if (right > windowWidth) {
      // Move the element back onto the screen
      left = windowWidth - (width + 5);
    }

    // Only update state if it's changed. This is necessary to avoid infinite
    // render loops from updating state inside of `componentDidUpdate`.
    if (
      left === this.state.left &&
      top === this.state.top &&
      maxHeight === this.state.maxHeight &&
      width === this.state.width
    ) {
      return false;
    }

    this.setState({ left, top, maxHeight, width });

    return true;
  };

  getIdealTopPosition(
    targetRectTop: number,
    offsetTop: number,
    windowHeight: number,
    popoverHeight: number,
  ) {
    const beneathTarget = targetRectTop + offsetTop;
    const aboveTarget = targetRectTop - popoverHeight - 6;
    const aboveBottomOfWindow = windowHeight - popoverHeight - 6;

    const doesNotOverflowWindow = (topPos: number) =>
      topPos >= HEADER_HEIGHT_OFFSET && topPos + popoverHeight <= windowHeight;

    const idealTopPosition =
      [beneathTarget, aboveTarget, aboveBottomOfWindow].find(
        doesNotOverflowWindow,
      ) || HEADER_HEIGHT_OFFSET;

    return idealTopPosition;
  }

  handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.pop();
  };

  handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    this.onClose();
  };

  handleOutsideClick = (e: MouseEvent) => {
    const targetNode = this.props.target && this.props.target();

    switch (true) {
      case e.defaultPrevented:
      case !this.popoverElem:
      case this.props.descendants.some((elem) =>
        elem.contains(e.target as Node),
      ):
      case !!this.popoverElem && this.popoverElem.contains(e.target as Node):
        /**
         * If the click was defaultPrevented, there is no popover element,
         * or if the click target is within the popover itself, do nothing.
         */
        break;
      case !!targetNode && targetNode === (e.target as Node):
      case !!targetNode && targetNode.contains(e.target as Node):
        /**
         * If the click target is a descendent of the popover target or
         * is the target itself, pop a layer off the popover.
         *
         * IMPORTANT: Calling stopPropagation here is relatively safe because
         * this handler is invoked during the capture phase of the click event
         * and won't prevent the popover trigger button's click handler from executing.
         */
        this.pop();
        e.stopPropagation();
        break;
      default:
        /**
         * If the click target is outside the popover and the popover target,
         * close the popover normally.
         */
        this.onClose('by clicking outside popover');
    }
  };

  handleEscapePress = (e: React.KeyboardEvent<HTMLElement>) => {
    const currentLayer = this.getCurrentLayer();

    if (currentLayer) {
      this.pop();
    } else if (this.props.descendants.length === 0) {
      this.onClose('by pressing escape key');
    }
  };

  debounceUpdateSizeAndPosition = debounce(this.updateSizeAndPosition, 300);

  onClose = (trackingMethod = '') => {
    this.hasClosed = true;
    if (this.props.onClose) {
      this.props.onClose(trackingMethod);
    } else {
      this.props.defaultOnClose();
    }
  };

  getWidthProp = () => {
    const currentLayer = this.getCurrentLayer();

    return currentLayer ? currentLayer.options.width : this.props.width;
  };

  getCurrentLayer = () => {
    const { stack } = this.state;
    const length = stack.length;

    return length > 0 ? stack[length - 1] : null;
  };

  scrollContentToTop = () => {
    if (this.contentRef.current) {
      this.contentRef.current.scrollTop = 0;
    }
  };

  getTitle = () => {
    const currentLayer = this.getCurrentLayer();

    if (currentLayer) {
      return currentLayer.options.title || '';
    }

    return this.props.title;
  };

  keybindings = {
    [Key.Escape]: {
      callback: this.handleEscapePress,
      preventDefault: true,
      stopPropagation: true,
      isGlobal: true,
    },
  };

  render() {
    const {
      addDescendant,
      className,
      contentClassName,
      name,
      showCloseButton,
      testId,
    } = this.props;
    const { left, maxHeight, top, width } = this.state;
    const { fixedContent } = this.props;

    const currentLayer = this.getCurrentLayer();

    const title = this.getTitle();

    const titlePadding = title ? POPOVER_WITH_TITLE_PADDING : 0;

    return (
      <LayerManagerPortal layer={Layers.Popover}>
        <Keybingings keybindings={this.keybindings}>
          <div
            className={classNames(
              styles.popover,
              className || styles.defaultPopover,
            )}
            data-test-id={testId}
            ref={(elem) => {
              this.popoverElem = elem;

              // Element being mounted into the DOM
              if (elem !== null) {
                addDescendant(elem);
              }
            }}
            style={{ left, top, width, maxHeight }}
            tabIndex={-1}
            role="dialog"
          >
            <PopoverHeader
              onClickBack={
                currentLayer ? preventDefault(() => this.pop()) : undefined
              }
              popoverName={name}
              showCloseButton={showCloseButton}
              title={title}
            />
            <Rect>
              {({ rect, ref }) => (
                <>
                  <div ref={ref}>{fixedContent}</div>
                  <div
                    className={classNames(
                      styles.popoverContent,
                      contentClassName || styles.defaultPopoverContent,
                      {
                        [styles.includePadding]: !!title,
                      },
                    )}
                    ref={this.contentRef}
                    style={{
                      maxHeight:
                        maxHeight -
                        POPOVER_PADDING -
                        titlePadding -
                        (rect ? rect.height : 0),
                    }}
                  >
                    {currentLayer
                      ? currentLayer.node
                      : this.props.children({
                          maxHeight,
                          pop: this.pop,
                          push: this.push,
                          closePopover: this.onClose,
                          pushConfirm: this.pushConfirm,
                        })}
                  </div>
                </>
              )}
            </Rect>
          </div>
        </Keybingings>
      </LayerManagerPortal>
    );
  }
}

interface PopoverDescendantState {
  descendants: HTMLElement[];
}

class PopoverContainer extends React.Component<
  Exclude<PopoverProps, ContextProps>,
  PopoverDescendantState
> {
  state: PopoverDescendantState = {
    descendants: [],
  };

  render() {
    return (
      <PopoverOwnerContext.Consumer>
        {({ addDescendant, removeDescendant }) => {
          return (
            <PopoverOwnerContext.Provider
              value={{
                // We wrap the context methods each time we render a
                // nested popover, calling the parent's method along
                // with the instance method. This means that each
                // descendant will be added and removed from all of
                // its ancestor popovers automatically
                addDescendant: (descendant: HTMLElement) => {
                  this.addDescendant(descendant);
                  addDescendant(descendant);
                },
                removeDescendant: (descendant: HTMLElement) => {
                  this.removeDescendant(descendant);
                  removeDescendant(descendant);
                },
              }}
            >
              <PopoverWithoutContext
                {...this.props}
                addDescendant={addDescendant}
                descendants={this.state.descendants}
                removeDescendant={removeDescendant}
              />
            </PopoverOwnerContext.Provider>
          );
        }}
      </PopoverOwnerContext.Consumer>
    );
  }

  addDescendant = (elem: HTMLElement) => {
    if (this.state.descendants.includes(elem)) {
      return;
    }

    this.setState({
      descendants: [...this.state.descendants, elem],
    });
  };

  removeDescendant = (descendant: HTMLElement) => {
    this.setState({
      descendants: this.state.descendants.filter((d) => d !== descendant),
    });
  };
}

const mapDispatchToProps = (dispatch: Dispatch<State>, ownProps: OwnProps) => {
  return {
    defaultOnClose() {
      dispatch(closePopover(ownProps.name));
    },
  };
};

export const Popover = connect(
  undefined,
  mapDispatchToProps,
)(PopoverContainer);



// WEBPACK FOOTER //
// ./app/gamma/src/components/popover/index.tsx